import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FadeUp, Eyebrow } from './Reveal';
import { EASE } from '../data/site';

const API = process.env.REACT_APP_BACKEND_URL;

const NOTE_COLORS = ['#F6EFE3', '#FFE9C7', '#FBD9DE', '#E4EAD9', '#E3E8F5'];

const Guestbook = () => {
  const [notes, setNotes] = useState([]);
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | done | error

  useEffect(() => {
    fetch(`${API}/api/notes`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setNotes)
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !message.trim() || status === 'sending') return;
    setStatus('sending');
    try {
      const r = await fetch(`${API}/api/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), message: message.trim() }),
      });
      if (!r.ok) throw new Error();
      const note = await r.json();
      setNotes((prev) => [note, ...prev].slice(0, 12));
      setName('');
      setMessage('');
      setStatus('done');
      setTimeout(() => setStatus('idle'), 3000);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <section className="px-5 md:px-10 lg:px-24 py-24 md:py-36" data-testid="guestbook-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Eyebrow color="#B50A00">The Guestbook</Eyebrow>
          <h2 className="mt-8 text-4xl md:text-6xl font-light tracking-tight leading-[1.05]">
            Leave a note<br />for the author.
          </h2>
          <p className="mt-6 text-base md:text-lg text-ink/70 leading-relaxed max-w-md">
            The book opened with a dedication to shopkeepers. This is where you write back,
            a thought, a memory of your corner store, a word for Aditya.
          </p>
          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="guestbook-form">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
              required
              placeholder="Your name"
              data-testid="guestbook-name-input"
              className="w-full bg-transparent border-b border-ink/25 focus:border-brand-red outline-none py-3 text-base md:text-lg placeholder:text-support/60 transition-colors duration-300"
            />
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={400}
              required
              rows={3}
              placeholder="Your note…"
              data-testid="guestbook-message-input"
              className="w-full bg-transparent border-b border-ink/25 focus:border-brand-red outline-none py-3 text-base md:text-lg placeholder:text-support/60 resize-none transition-colors duration-300"
            />
            <div className="flex items-center gap-5">
              <button
                type="submit"
                disabled={status === 'sending'}
                data-testid="guestbook-submit-button"
                className="border border-ink px-8 py-3.5 text-xs font-medium uppercase tracking-[0.25em] transition-colors duration-500 hover:bg-brand-red hover:border-brand-red hover:text-paper disabled:opacity-50"
              >
                {status === 'sending' ? 'Pinning…' : 'Pin the note'}
              </button>
              <AnimatePresence>
                {status === 'done' && (
                  <motion.span
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0 }}
                    className="text-sm text-brand-green"
                    data-testid="guestbook-success"
                  >
                    Pinned to the guestbook.
                  </motion.span>
                )}
                {status === 'error' && (
                  <motion.span
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0 }}
                    className="text-sm text-brand-red"
                    data-testid="guestbook-error"
                  >
                    Could not pin it, try again.
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
          </form>
        </div>

        <div className="lg:col-span-7">
          {notes.length === 0 ? (
            <FadeUp className="h-full min-h-[280px] border border-dashed border-ink/20 flex items-center justify-center p-10">
              <p className="text-support text-sm uppercase tracking-[0.25em] text-center" data-testid="guestbook-empty">
                No notes yet,<br />yours could be the first on the wall
              </p>
            </FadeUp>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="guestbook-notes">
              {notes.map((n, i) => (
                <motion.figure
                  key={n.id}
                  initial={{ opacity: 0, y: 24, rotate: 0 }}
                  animate={{ opacity: 1, y: 0, rotate: (i % 2 === 0 ? -1 : 1) * 1.2 }}
                  transition={{ duration: 0.6, ease: EASE, delay: Math.min(i * 0.05, 0.3) }}
                  className="p-7 shadow-md"
                  style={{ background: NOTE_COLORS[i % NOTE_COLORS.length] }}
                  data-testid={`guestbook-note-${i}`}
                >
                  <blockquote className="text-base md:text-lg leading-relaxed text-ink/85">
                    "{n.message}"
                  </blockquote>
                  <figcaption className="mt-5 text-xs uppercase tracking-[0.25em] text-support">
                   , {n.name}
                  </figcaption>
                </motion.figure>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Guestbook;
